import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const faqs = [
  {
    q: 'How much does DanceHall Studios cost?',
    a: 'Free for studios — always. Students pay a 6% service fee on their course fee. Stripe processing comes out of that 6%, and whatever is left goes back to you as cash back. You keep 100% of the price you set.',
  },
  {
    q: 'How do payments work?',
    a: 'Powered by Stripe. Students pay online with credit card or ACH bank transfer. Monthly recurring or one-time — money goes directly to your bank account.',
  },
  {
    q: 'What types of studios can use DanceHall Studios?',
    a: 'Dance, music, yoga, martial arts, art, fitness — any studio that teaches classes. If you have students and courses, DanceHall Studios works for you.',
  },
  {
    q: 'Can I use it for online classes?',
    a: 'Yes. Zoom integration lets you create meetings, track attendance, and let students join with one click. In-person, online, or hybrid — your choice.',
  },
  {
    q: 'How does attendance tracking work?',
    a: 'For in-person classes: students scan a QR code when they arrive. For online: Zoom auto-tracks who joins. Full attendance history and reports are built in.',
  },
  {
    q: 'Is there a contract or lock-in?',
    a: "No contracts, no lock-in, no hidden fees. You can stop using DanceHall Studios at any time. Your students' payments are processed directly through your Stripe account.",
  },
];

const LandingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const { login } = useAuth();

  const signupAs = (role) => {
    sessionStorage.setItem('dancehall_signup_role', role);
    login({ mode: 'signup' });
  };

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <div className="min-h-screen bg-[#faf0f0] font-body">

      {/* ─── Nav ─── */}
      <nav className="fixed top-0 w-full bg-[#faf0f0]/80 backdrop-blur-md z-50 border-b border-gray-200/60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-heading text-2xl tracking-tight text-gray-900"><span className="font-bold">DanceHall</span> Studios</span>

          <div className="hidden md:flex items-center gap-8 text-[13px] text-gray-500">
            <a href="#features" className="hover:text-gray-900 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-gray-900 transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-gray-900 transition-colors">FAQ</a>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => login()}
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Log in
            </button>
            <button
              onClick={() => signupAs('teacher')}
              className="text-sm font-medium bg-brand-900 text-white px-5 py-2 rounded-full hover:bg-brand-800 transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="pt-32 pb-20 md:pt-44 md:pb-32 px-6 relative overflow-hidden">
        {/* Subtle gradient wash */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-gradient-to-br from-brand-100/40 via-transparent to-saffron-100/30 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center relative">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-brand-50 border border-brand-200/80 text-brand-800 px-4 py-1.5 rounded-full text-sm font-medium mb-8">
            <span>Transparent pricing</span>
            <span className="text-brand-300">&middot;</span>
            <span>Free for studios</span>
          </div>

          <h1 className="font-heading text-5xl md:text-7xl text-gray-900 mb-8 leading-[1.08]">
            Run your studio.
            <br />
            <span className="text-brand-900">Not spreadsheets.</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            Enrollment, payments, attendance, class materials, and communication
            &mdash; all in one place. Set up in 10 minutes.
          </p>

          <div className="flex items-center justify-center">
            <button
              onClick={() => signupAs('teacher')}
              className="bg-brand-900 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-brand-800 transition-all hover:shadow-lg hover:shadow-brand-900/25"
            >
              Create Your Studio
            </button>
          </div>

        </div>
      </section>

      {/* ─── Pain Points ─── */}
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900 mb-4">Sound familiar?</h2>
            <p className="text-gray-500 text-lg">The problems every studio owner knows too well.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Chasing payments */}
            <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:border-gray-200 transition-colors">
              <svg className="w-8 h-8 text-brand-900 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Stop chasing payments</h3>
              <p className="text-gray-500 leading-relaxed">
                &ldquo;I'll Venmo you later&rdquo; shouldn't be your business model.
                Students enroll and pay automatically &mdash; monthly, quarterly, or one-time.
              </p>
            </div>

            {/* Spreadsheet attendance */}
            <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:border-gray-200 transition-colors">
              <svg className="w-8 h-8 text-brand-900 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Ditch the attendance spreadsheet</h3>
              <p className="text-gray-500 leading-relaxed">
                Students scan a QR code when they walk in. Zoom classes auto-tracked.
                Full history at a glance. 15 seconds, not 15 minutes.
              </p>
            </div>

            {/* WhatsApp chaos */}
            <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:border-gray-200 transition-colors">
              <svg className="w-8 h-8 text-brand-900 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">End the WhatsApp chaos</h3>
              <p className="text-gray-500 leading-relaxed">
                No more scrolling through group chats for class videos.
                Materials organized by week, reminders automated, everything in one place.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Features (bento grid) ─── */}
      <section id="features" className="py-20 md:py-28 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl text-gray-900 mb-4">
              Everything you need.
              <br className="hidden md:block" />
              Nothing you don't.
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Built by studio owners, for studio owners. Every feature exists because someone needed it.
            </p>
          </div>

          {/* Row 1 — two large cards */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Payments */}
            <div className="bg-[#faf0f0] rounded-2xl p-8 md:p-10 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-8 h-8 text-brand-900 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="font-heading text-2xl text-gray-900 mb-3">Payments on autopilot</h3>
              <p className="text-gray-500 leading-relaxed mb-4">
                Powered by Stripe. Monthly recurring, one-time, or block pricing. Credit card and
                ACH bank transfer. Money goes directly to your bank account.
              </p>
              <p className="text-sm font-medium text-brand-900">Never forget anyone's payment again.</p>
            </div>

            {/* Attendance */}
            <div className="bg-[#faf0f0] rounded-2xl p-8 md:p-10 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-8 h-8 text-brand-900 mb-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}>
                <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" />
                <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" />
                <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" />
                <rect x="14" y="14" width="3" height="3" stroke="currentColor" />
                <rect x="18" y="14" width="3" height="3" stroke="currentColor" />
                <rect x="14" y="18" width="3" height="3" stroke="currentColor" />
                <rect x="18" y="18" width="3" height="3" stroke="currentColor" />
                <rect x="5.5" y="5.5" width="2" height="2" fill="currentColor" />
                <rect x="16.5" y="5.5" width="2" height="2" fill="currentColor" />
                <rect x="5.5" y="16.5" width="2" height="2" fill="currentColor" />
              </svg>
              <h3 className="font-heading text-2xl text-gray-900 mb-3">QR code attendance</h3>
              <p className="text-gray-500 leading-relaxed mb-4">
                Students scan a code when they walk in. Done in 15 seconds.
                Zoom classes auto-tracked. Full attendance history, reports, and analytics built in.
              </p>
              <p className="text-sm font-medium text-brand-900">In-person + online, one system.</p>
            </div>
          </div>

          {/* Row 2 — three medium cards */}
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            {/* Materials */}
            <div className="bg-[#faf0f0] rounded-2xl p-8 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-7 h-7 text-brand-900 mb-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Class materials</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Upload tutorials once. Videos unlock week by week.
                Watermarked with each student's name. No raw links shared.
              </p>
            </div>

            {/* WhatsApp + Email */}
            <div className="bg-[#faf0f0] rounded-2xl p-8 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-7 h-7 text-brand-900 mb-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">WhatsApp & Email</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Class reminders, payment nudges, enrollment confirmations.
                Students get notified where they already are.
              </p>
            </div>

            {/* Zoom */}
            <div className="bg-[#faf0f0] rounded-2xl p-8 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-7 h-7 text-brand-900 mb-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Zoom integration</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Create meetings, auto-track attendance.
                In-person, online, or hybrid &mdash; one click to join.
              </p>
            </div>
          </div>

          {/* Row 3 — full-width accent card */}
          <div className="bg-brand-900 rounded-2xl p-8 md:p-10 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h3 className="font-heading text-2xl mb-2">Free studio website</h3>
                <p className="text-brand-100 leading-relaxed max-w-xl">
                  Beautiful enrollment page with your branding. Share one link &mdash; students see your
                  courses, sign up, and pay. No web designer needed.
                </p>
              </div>
              <button
                onClick={() => signupAs('teacher')}
                className="shrink-0 bg-white text-brand-600 px-6 py-3 rounded-full font-semibold hover:bg-brand-50 transition-colors"
              >
                See it in action
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900">Up and running in 10 minutes</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative">
            {/* Connector line (desktop only) */}
            <div className="hidden md:block absolute top-7 left-[calc(16.67%+28px)] right-[calc(16.67%+28px)] h-px bg-gray-200" />

            {[
              { n: '1', title: 'Create your studio', desc: 'Set up your profile, add your programs and courses. Name your levels, set your schedule.' },
              { n: '2', title: 'Set your pricing', desc: 'Monthly, block, or workshop. Set your price — you keep 100% of it. A 6% service fee covers DanceHall and Stripe fees, with cash back to you.' },
              { n: '3', title: 'Share your link', desc: 'Students enroll and pay online. You focus on teaching. We handle the rest.' },
            ].map((step) => (
              <div key={step.n} className="text-center relative">
                <div className="w-14 h-14 bg-white border-2 border-brand-200 rounded-2xl flex items-center justify-center mx-auto mb-6 relative z-10">
                  <span className="font-heading text-2xl text-brand-500">{step.n}</span>
                </div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Who It's For ─── */}
      <section className="py-20 md:py-28 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900 mb-4">Built for studios of every kind</h2>
            <p className="text-gray-500 text-lg">If you teach classes and have students, DanceHall Studios works for you.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: 'Dance', icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
                </svg>
              )},
              { name: 'Music', icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
                </svg>
              )},
              { name: 'Yoga', icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                </svg>
              )},
              { name: 'Martial Arts', icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                </svg>
              )},
              { name: 'Art', icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
                </svg>
              )},
              { name: 'Fitness', icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z" />
                </svg>
              )},
            ].map((type) => (
              <div key={type.name} className="text-center p-6 rounded-2xl bg-[#faf0f0] border border-gray-100 hover:border-brand-200 transition-colors group">
                <div className="text-gray-400 group-hover:text-brand-500 transition-colors flex justify-center mb-3">
                  {type.icon}
                </div>
                <p className="font-medium text-gray-700 text-sm">{type.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section id="pricing" className="py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-5xl text-gray-900 mb-4">Free for studios. Always.</h2>
            <p className="text-gray-500 text-lg">
              No tiers. No upsells. Every feature included.
              <br />
              We add <span className="font-semibold text-gray-900">2.99%</span> on student payments &mdash; that's all. You keep 100% of your price.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-8 md:p-10">
            <div className="flex items-baseline gap-2 mb-8">
              <span className="font-heading text-5xl text-gray-900">$0</span>
              <span className="text-gray-400 text-lg">/month, forever</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mb-10">
              {[
                'Unlimited courses',
                'Unlimited students',
                'Stripe-powered payments',
                'QR code attendance',
                'Class materials & videos',
                'Email notifications',
                'WhatsApp integration',
                'Free studio website',
                'Zoom integration',
                'Cash back program',
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-brand-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  <span className="text-gray-600">{feature}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => signupAs('teacher')}
              className="w-full bg-brand-900 text-white py-4 rounded-full font-semibold text-lg hover:bg-brand-800 transition-colors"
            >
              Create Your Studio
            </button>

          </div>

          {/* Transparent pricing callout */}
          <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-8">
            <h4 className="font-heading text-xl text-gray-900 mb-2">Transparent pricing</h4>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">
              A 6% service fee is added to each payment. This covers Stripe processing and DanceHall's 2.99% cut.
              Whatever remains goes back to you as cash back.
            </p>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="py-20 md:py-28 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900">Questions & answers</h2>
          </div>

          <div className="divide-y divide-gray-100">
            {faqs.map((faq, i) => (
              <div key={i}>
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full flex items-center justify-between py-6 text-left group"
                  aria-expanded={openFaq === i}
                >
                  <span className="font-medium text-gray-900 pr-8 group-hover:text-brand-500 transition-colors">
                    {faq.q}
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-400 shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-45' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </button>
                <div className={`overflow-hidden transition-all duration-200 ${openFaq === i ? 'max-h-48 pb-6' : 'max-h-0'}`}>
                  <p className="text-gray-500 leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-brand-900 rounded-3xl px-8 py-16 md:px-16 md:py-24 text-center relative overflow-hidden">
            {/* Decorative gradients */}
            <div className="absolute inset-0 bg-gradient-to-br from-brand-800/50 via-transparent to-saffron-500/10 pointer-events-none" />
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-700/30 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-saffron-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative">
              <h2 className="font-heading text-3xl md:text-5xl text-white mb-6 leading-tight">
                Ready to focus on
                <br />
                what you love?
              </h2>
              <p className="text-brand-200 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
                Join studios that have already simplified their workflow.
                Set up takes 10 minutes. It's free.
              </p>
              <button
                onClick={() => signupAs('teacher')}
                className="bg-white text-brand-900 px-10 py-4 rounded-full text-lg font-semibold hover:bg-brand-50 transition-colors hover:shadow-lg"
              >
                Create Your Studio
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="bg-[#111111] text-gray-400 rounded-t-[2rem] mt-8">
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-8">
          <div className="flex flex-col md:flex-row gap-12 md:gap-0 mb-16">
            {/* Logo — large, bold */}
            <div className="md:w-2/5">
              <h2 className="font-heading text-4xl md:text-5xl text-white leading-tight">
                <span className="font-bold">DanceHall</span>
                <span className="font-light text-gray-500"> Studios</span>
              </h2>
            </div>

            {/* Links */}
            <div className="md:w-1/5">
              <ul className="space-y-3 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li>
                  <button onClick={() => signupAs('teacher')} className="hover:text-white transition-colors">
                    Get Started
                  </button>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div className="md:w-1/5">
              <div className="flex items-start gap-2 text-sm mb-3">
                <svg className="w-4 h-4 mt-0.5 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5M3.75 3v18m16.5-18v18M5.25 3h13.5M6 6.75h.75m-.75 3h.75m-.75 3h.75m3-6.75h.75m-.75 3h.75m-.75 3h.75m3-6.75h.75m-.75 3h.75m-.75 3h.75M6 18.75h12" />
                </svg>
                <a href="https://www.cbblabs.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  CBB Labs Inc.
                </a>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <svg className="w-4 h-4 mt-0.5 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
                <span>447 Broadway FL 2, New York, NY</span>
              </div>
            </div>

            {/* Follow Us */}
            <div className="md:w-1/5 md:text-right">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">Follow Us</p>
              <div className="flex items-center gap-3 md:justify-end">
                <a
                  href="https://www.instagram.com/my.dancehall/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center hover:border-white hover:text-white transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-gray-800 pt-6 text-center">
            <p className="text-xs text-gray-600">
              &copy; {new Date().getFullYear()} DanceHall Studios. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
